import WalkHelper from '../../helper/WalkHelper.js';
import Promissory from '../../helper/util/Promissory.js';
import VersionHelper from '../../helper/VersionHelper.js';
// <remove-on-release>
// Deprecated, remove in 7.0
// </remove-on-release>
const
    countChildren = parent => parent.children?.reduce((sum, child) => sum + (child.remoteChildCount ?? 0) + 1, 0) ?? 0,
    legacyIsFullyLoaded = parent => countChildren(parent) >= parent.remoteChildCount;

export default Target => class TreeStoreLazyLoadPlugin extends Target {
    static $name = 'TreeStoreLazyLoadPlugin';

    static get pluginConfig() {
        const config = {};

        for (const prop in super.pluginConfig) {
            config[prop] = [...super.pluginConfig[prop]];
        }
        config.override = config.override ?? [];
        config.override.push('loadChildren', 'getAt');

        // lazyGetAt has no practical functionality for these stores
        const lazyGetAt = config.assign?.indexOf('lazyGetAt');
        if (lazyGetAt >= 0) {
            config.assign.splice(lazyGetAt, 1);
        }

        return config;
    }

    static configurable = {
        chunkSize : 50
    };

    constructor(...args) {
        super(...args);
        this.totalCount = 0; // Tells RowManager not to render placeholders
    }

    // Need to overwrite default load, because it expects a promise
    async load(params) {
        const
            me = this,
            isReload = me._loadedOnce;

        // If the client is autoLoad and this is not a reload, it is probably the autoLoad call, and if so,
        // we wait 1 frame to let everything be created
        if (me.client.autoLoad && !isReload) {
            await new Promise(resolve => me.requestAnimationFrame(resolve));
        }

        me._loadedOnce = true;
        me._isReloading = isReload;

        return me.doLazyLoad({ parent : me.client.rootNode, isReload, startIndex : 0, ...params });
    }

    // Updates a parent's remoteChildCount by adding the specified amount
    // Also updates the parent's parents the whole way up to the rootNode
    // <remove-on-release>
    // Remove in 7.0
    // </remove-on-release>
    changeRemoteChildCount(records, amount, affectedParents) {
        if (records?.length) {
            const parents = records.map(r => r.parent ?? this.client.getById(r.parentId ?? r.originalData?.parentId));

            while (parents.length) {
                const parent = parents.shift();
                if (parent) {
                    if (!parent.isRoot) {
                        parents.push(parent.parent);
                    }

                    parent.set('remoteChildCount', (parent.remoteChildCount ? parent.remoteChildCount + amount : parent.children.length) ?? 0, true);

                    affectedParents?.add(parent);
                }
            }
        }
    }

    // When a record is added or removed
    internalOnCommit({ changes }) {
        const
            me = this,
            { $loadLock, client } = me;

        // Enable the loading of data upon commit
        if ($loadLock?.size) {
            [...$loadLock.keys()].forEach(r => {
                if (!client.added.some(a => a.parent === r) && !client.removed.some(a => a.parent === r)) {
                    $loadLock.get(r).resolve();
                    $loadLock.delete(r);
                }
            });
        }

        // <remove-on-release>
        // Deprecated, completely remove this block in 7.0
        // </remove-on-release>
        if (me.$legacyCountMode) {
            const affectedParents = new Set();

            // Increase remoteChildCount for all changed record's parents (and their parents etc.)
            me.changeRemoteChildCount(changes.added, 1, affectedParents);
            me.changeRemoteChildCount(changes.removed, -1, affectedParents);

            // Reset the loadQueue for each affected parent
            affectedParents.forEach(parent => {
                parent.isFullyLoaded = legacyIsFullyLoaded(parent);
            });
        }
    }

    // If we have any uncommited changes, we are not allowed to load more data
    internalOnChange() {
        const
            { $loadLock = (this.$loadLock = new Map()) } = this,
            { added, removed } = this.client;

        if (added.count || removed.count) {
            const parents = [...new Set(added.map(r => r.parent).concat(removed.map(r => r.parent)))];
            parents.forEach(parent => !$loadLock.get(parent) && $loadLock.set(parent, new Promissory()));

        }
    }

    // Implementing the load on demand function
    async loadChildren(parent) {
        return this.doLazyLoad({ parent, startIndex : 0 });
    }

    // Overrides store default getAt
    getAt(index) {
        const record = this.client.records[index];

        record && !this._isReloading && this.loadMoreChildren(record);

        return record;
    }

    async loadMoreChildren(record) {
        const
            me = this,
            { parent }  = record;

        // Autoload children of requested expanded parents
        if (record.expanded && !record.children?.length && !record.isFullyLoaded && !me.loadQueue[record.id]) {
            me.doLazyLoad({ parent : record, startIndex : 0 });
        }

        // Autoload more children if lastChild is requested and parent is not fully loaded
        if (record === parent.lastChild && !parent.isFullyLoaded && !me.loadQueue[parent.id]) {
            // Check if there is a load lock for this parent (if it got added/removed children)
            const loadLock = me.$loadLock?.get(parent);
            if (loadLock) {
                // If so, wait for that to resolve before loading more children
                const meta = parent.instanceMeta(me.client);
                meta.isLoadingChildren = true; // Shows a loading spinner on the parent row
                await loadLock.promise;

                if (me.isDestroyed) {
                    return;
                }

                if (parent.isFullyLoaded) {
                    meta.isLoadingChildren = false;
                    me.client.trigger?.('refresh', { action : 'lazyload' });
                    return;
                }
            }
            me.doLazyLoad({ startIndex : record.parentIndex + 1, parent });

        }
    }

    async doLazyLoad({ parent, isReload, ...params }) {
        isReload && this.clear();

        const
            me         = this,
            { client } = me,
            { id }     = parent;

        if (me.isDestroyed) {
            return;
        }

        parent.instanceMeta(client).isLoadingChildren = true;
        me.triggerLazyLoadStart(arguments[0]);

        // Save the promise in the queue
        const promise = me.loadQueue[id] = client.requestData({
            ...(client.readUrl ? params : client.buildRemoteParams(params)),
            parentId : parent.isRoot ? 'root' : id,
            count    : me.chunkSize,
            ...(me.$isCrudManager && { isReload })
        });

        const {
            [me.totalCountProperty] : totalCount,
            [me.dataProperty]       : data,
            ...additionalData
        } = await promise;

        if (me.isDestroyed || me.loadQueue[id] !== promise) {
            // A reload request has been made during another request
            return;
        }

        delete me.loadQueue[id];

        // Detect legacyCountMode on the first loaded record only
        if (parent.isRoot && data.length && me.$legacyCountMode === undefined) {
            if ((me.$legacyCountMode = data[0].remoteChildCount >= 0) && !VersionHelper.isTestEnv) {
                VersionHelper.deprecate('core', '7.0.0', 'remoteChildCount is deprecated in favor of autodetection and the isFullyLoaded field');
            }
        }

        if (parent.isRoot && totalCount >= 0 && me.$legacyCountMode) {
            // Set totalCount on the rootNode
            parent.set('remoteChildCount', totalCount, true);
        }

        me.addData(data, parent, isReload);

        parent.set('isFullyLoaded', additionalData.isFullyLoaded ?? (me.$legacyCountMode ? legacyIsFullyLoaded(parent) : data.length < me.chunkSize), true);

        me.totalCount = client.count;

        if (isReload) {
            me._isReloading = false;
        }

        if (!me.isLoading) {
            client.trigger?.('refresh', { action : 'lazyload' });
        }

        me.triggerLazyLoadEnd(arguments[0], data);
        parent.instanceMeta(client).isLoadingChildren = false;

    }

    addData(data, parent, isReload) {
        const
            { client, $legacyCountMode } = this,
            clearFromAdded               = [];

        if (!client.modelClass.fieldMap.isFullyLoaded) {
            // Add a isFullyLoaded field to the model class
            client.modelClass.addField({ name : 'isFullyLoaded', persist : false });
        }

        // <remove-on-release>
        // Deprecated, completely remove this block in 7.0
        // </remove-on-release>
        if ($legacyCountMode) {
            if (!client.modelClass.fieldMap.remoteChildCount) {
                // Add a remoteChildCount field to the model class
                client.modelClass.addField('remoteChildCount');
            }

            if (!data.length) {
                // If we got no rows, something is wrong with the provided remote counts
                // we update them to not send a bunch of unnecessary load requests
                const
                    childCount     = countChildren(parent),
                    childCountDiff = parent.remoteChildCount - childCount;

                if (childCountDiff !== 0) {
                    parent.set('remoteChildCount', childCount, true);
                    this.changeRemoteChildCount([parent], childCountDiff);
                }
            }
        }

        WalkHelper.preWalk({ children : data, $fake : true }, ({ children }) => children?.length ? children : null, record => {
            const { expanded } = record;

            if (!record.$fake) {
                clearFromAdded.push(record);
            }

            if ((expanded != null && !$legacyCountMode) || record.children || record.remoteChildCount > 0) {
                // If a parent is unloaded but its children kept cached, add those
                this.$removedDescendants?.forEach(d => {
                    if (d.parentId === record.id && !record.children?.some?.(c => c.id === d.id)) {
                        record.children.push(d);
                        clearFromAdded.push(d);
                        if (d.meta.parentWasFullyLoaded) {
                            record.isFullyLoaded = true;
                        }
                    }
                });

                // Setting children = true will make the parent lazyLoad its children when it is expanded
                if (expanded === false && !record.children?.length) {
                    record.children = true;
                }
                // If server provided children to the requested nodes, check if the nodes are fully loaded
                // Deprecated, remove this block in 7.0
                else if ($legacyCountMode && record.children?.length && !record.isFullyLoaded && legacyIsFullyLoaded(record)) {
                    // If so, mark them as that
                    record.isFullyLoaded = true;
                }
                // Makes the record pass as a parent
                else if (!record.children) {
                    record.children = [];
                }
            }
        });

        if (isReload) {
            client.data = data;
        }
        else {
            parent.appendChild(data, true);
            client.added.remove(clearFromAdded);
        }
    }

    unload() {
        super.unload(...arguments);
        this.client.trigger('refresh', { action : 'lazyload' });
    }

    internalUnloadRecord({ record, includeDescendants, cacheDescendants }) {
        if (!record.isParent) {
            return;
        }

        const
            me = this,
            {
                loadQueue,
                client,
                $removedDescendants = (me.$removedDescendants = [])
            } = me;

        if (record.children?.length) {
            record.traverse(child => me.internalUnloadRecord({ record : child, includeDescendants, cacheDescendants : !includeDescendants }), true);

            const children = [...record.children];
            record.clearChildren(true);
            client.removed.remove(children);

            if (cacheDescendants) {
                $removedDescendants.push(...children);
                children[0].meta.parentWasFullyLoaded = record.isFullyLoaded;
            }
        }

        record.isFullyLoaded = false;
        loadQueue[record.id] = undefined;
    }
};
